import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus, ChevronDown, ChevronRight, CheckCircle2, Circle,
  Clock, Ban, Pencil, Trash2, X, Save, GripVertical, Lock, Search,
  CornerDownRight,
} from "lucide-react";
import { productColor } from "@/lib/productColors";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, closestCenter,
  type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/cn";
import { orderWithSubtasks, groupSubtasks } from "@/lib/taskTree";
import { shortDate } from "@/lib/format";
import { toast } from "sonner";
import {
  useMilestones, useAddMilestone, useUpdateMilestone, useDeleteMilestone,
  useAllMilestones, useAddMilestoneBlocker, useRemoveMilestoneBlocker,
  type Milestone, type ParentType, type BlockerRef,
} from "@/hooks/useMilestones";
import {
  useTasks, useCreateTask, useUpdateTask, useCompleteTask, useDeleteTask,
  useAddTaskBlocker, useRemoveTaskBlocker,
  type Task, type TaskBlockerRef,
} from "@/hooks/useTasks";
import { useProject, useUpdateProject } from "@/hooks/usePortfolio";
import api from "@/lib/api";
import { PersonPicker } from "@/components/ui/PersonPicker";
import type { Project } from "@/types/project";

const STATUS_LABEL: Record<string, string> = {
  not_started: "Not started", in_progress: "Working", blocked: "Blocked", complete: "Complete",
};
const STATUS_BG: Record<string, string> = {
  not_started: "bg-raised text-ink-3", in_progress: "bg-sky-100 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300",
  blocked: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300", complete: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
};
const STATUS_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  not_started: Circle, in_progress: Clock, blocked: Ban, complete: CheckCircle2,
};
const PRIORITY_BG: Record<string, string> = {
  Highest: "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300", High: "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
  Medium: "bg-raised text-ink-2", Low: "bg-canvas text-ink-muted",
};

// ---------------------------------------------------------------------------
// Canonical milestones — same six on every project. v1: hard-coded, no
// per-project customization, no add/delete in the UI. The whole point of
// using a fixed list is cross-portfolio comparability.
// ---------------------------------------------------------------------------
interface CanonicalMilestone {
  title: string;
  /** Pull initial due_date from this project field if present. */
  dueFrom?: keyof Project;
  /** Pull initial completed_date from this project field if present. */
  doneFrom?: keyof Project;
}

const CANONICAL_MILESTONES: CanonicalMilestone[] = [
  { title: "Kickoff",                  dueFrom: "start_date" },
  { title: "Functional Spec Complete", dueFrom: "functional_spec_due", doneFrom: "functional_spec_completed" },
  { title: "Technical Spec Complete",  dueFrom: "technical_spec_due",  doneFrom: "technical_spec_completed" },
  { title: "Build Complete" },
  { title: "UAT Signoff" },
  { title: "Go-Live",                  dueFrom: "end_date",            doneFrom: "actual_end" },
];

function dateColor(due: string | null, done: string | null): { fill: string; ring: string; label: string } {
  if (done) return { fill: "fill-emerald-500", ring: "stroke-emerald-700", label: "Complete" };
  if (!due) return { fill: "fill-ink-faint",   ring: "stroke-ink-muted",   label: "Not set" };
  const dueMs = new Date(due).getTime();
  if (Number.isNaN(dueMs)) return { fill: "fill-ink-faint", ring: "stroke-ink-muted", label: "Not set" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diffDays = (dueMs - today.getTime()) / 86400000;
  if (diffDays < 0)   return { fill: "fill-red-500",   ring: "stroke-red-700",   label: "Overdue" };
  if (diffDays <= 7)  return { fill: "fill-amber-500", ring: "stroke-amber-700", label: "Due soon" };
  return { fill: "fill-ink-subtle", ring: "stroke-ink-3", label: "Upcoming" };
}

// ---------------------------------------------------------------------------
// Task health — auto-derived from data we already have. NOT user-editable.
//
// Rules:
//   Red    — status == 'blocked'  OR  not complete AND end_date in past
//   Yellow — not complete AND end_date within 7 days
//                            OR  in_progress AND no updates in 14 days
//   Green  — in_progress AND end_date > 7 days away
//   null   — not_started (nothing promised yet) OR complete (done)
//
// Why derived instead of a manual override field: the manual field went
// stale immediately (Brett couldn't tell what the dot was for, and that's
// when WE built it). Modern tools (Monday, Smartsheet, MS Project) all
// auto-derive from data the system already has. The PM's escape valve
// for a vendor-stuck-but-not-overdue task is to set status=blocked —
// semantically correct AND it makes the dot red.
//
// The `health` column on project_tasks/initiative_tasks remains in the DB
// (added in the prior commit) but is no longer read or written by the
// UI. Flagged for cleanup once we trust the derived approach.
// ---------------------------------------------------------------------------
type DerivedHealth = "green" | "yellow" | "red" | null;

const HEALTH_STYLE: Record<string, { dot: string; ring: string; label: string }> = {
  none:   { dot: "bg-muted",   ring: "ring-line",  label: "No flag" },
  green:  { dot: "bg-emerald-500", ring: "ring-emerald-300", label: "On track" },
  yellow: { dot: "bg-amber-500",   ring: "ring-amber-300",   label: "At risk" },
  red:    { dot: "bg-red-500",     ring: "ring-red-300",     label: "Needs attention" },
};

function deriveTaskHealth(task: Task): DerivedHealth {
  const status = (task.status ?? "not_started").toLowerCase();
  if (status === "complete" || status === "not_started") return null;
  if (status === "blocked") return "red";

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = task.end_date ? new Date(task.end_date) : null;
  if (end) end.setHours(0, 0, 0, 0);

  // Overdue → red
  if (end && end.getTime() < today.getTime()) return "red";
  // Due soon → yellow
  if (end) {
    const days = (end.getTime() - today.getTime()) / 86400000;
    if (days <= 7) return "yellow";
  }
  // Stale in-progress (>14 days since last update) → yellow
  if (task.updated_at) {
    const last = new Date(task.updated_at);
    const days = (today.getTime() - last.getTime()) / 86400000;
    if (days > 14) return "yellow";
  }
  return "green";
}

function HealthDot({ health }: { health: DerivedHealth }) {
  const style = HEALTH_STYLE[health ?? "none"];
  return (
    <span
      title={style.label}
      aria-label={`Health: ${style.label}`}
      className={cn(
        "h-2.5 w-2.5 shrink-0 rounded-full ring-2 ring-offset-1 ring-offset-white",
        style.dot,
        style.ring,
      )}
    />
  );
}

export function PlanPanel({
  projectId,
  parentType = "project",
}: {
  projectId: string;
  parentType?: ParentType;
}) {
  const milestonesQ = useMilestones(projectId, parentType);
  const tasksQ = useTasks(projectId, parentType);
  // Project record is only used to seed canonical milestones from project
  // fields (start_date, *_spec_due, etc.) and to read use_products.
  const projectQ = useProject(parentType === "project" ? projectId : "");
  const useProductsFlag = Boolean(projectQ.data?.use_products);
  // Mutation to flip use_products on the parent record. Patching is
  // immediate — single field, no form. React Query refetches the parent
  // and the productsEnabled prop downstream picks it up.
  const updateProject = useUpdateProject();
  const toggleUseProducts = () => {
    updateProject.mutate({ id: projectId, use_products: !useProductsFlag });
  };
  const createMilestone = useAddMilestone(projectId, parentType);
  const updateMilestone = useUpdateMilestone(projectId, parentType);
  const deleteMilestone = useDeleteMilestone(projectId, parentType);
  const addBlocker = useAddMilestoneBlocker(projectId, parentType);
  const removeBlocker = useRemoveMilestoneBlocker(projectId, parentType);
  // Lazy: only loaded when the blocker picker opens (BlockerPicker calls
  // useAllMilestones itself). Listed here in the planning comment only.
  const completeTask = useCompleteTask(projectId, parentType);
  const createTask = useCreateTask(projectId, parentType);
  const updateTask = useUpdateTask(projectId, parentType);
  const deleteTask = useDeleteTask(projectId, parentType);
  const addTaskBlocker = useAddTaskBlocker(projectId, parentType);
  const removeTaskBlocker = useRemoveTaskBlocker(projectId, parentType);

  const allRows = milestonesQ.data ?? [];
  const tasks = tasksQ.data ?? [];

  // The parent's product list is EMERGENT — the union of product tags
  // across all tasks. Used to populate autocomplete suggestions when
  // adding a product to a task.
  const productSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      for (const p of t.products ?? []) {
        const trimmed = p?.trim();
        if (trimmed) set.add(trimmed);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  // ---- Drag-and-drop reordering -----------------------------------------
  // Tasks can be reordered within a phase or moved across phases via drag.
  // Phases themselves stay in their canonical SDLC order — they're
  // structural, not orderable.
  //
  // sort_order math uses a midpoint algorithm: when a task lands between
  // two neighbors, its new sort_order becomes the midpoint of theirs. This
  // means each drop is one PATCH (just the moved task), not a cascade of
  // updates across the whole phase.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const activeDragTask = useMemo(
    () => (activeDragId != null ? tasks.find((t) => t.id === activeDragId) ?? null : null),
    [activeDragId, tasks],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = Number(event.active.id);
    if (!Number.isNaN(id)) setActiveDragId(id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = Number(active.id);
    const activeTask = tasks.find((t) => t.id === activeId);
    if (!activeTask) return;

    const overIdStr = String(over.id);
    let targetMilestoneId: number | null;
    let targetTaskId: number | null = null;

    if (overIdStr.startsWith("phase-")) {
      // Dropped on a phase header (empty phase) or the "Unassigned" bucket
      const phaseStr = overIdStr.replace("phase-", "");
      targetMilestoneId = phaseStr === "null" ? null : Number(phaseStr);
    } else {
      // Dropped on another task
      targetTaskId = Number(overIdStr);
      const overTask = tasks.find((t) => t.id === targetTaskId);
      if (!overTask) return;
      targetMilestoneId = overTask.milestone_id;
    }

    if (targetTaskId === activeId) return;

    // Sorted target group minus the moved task — the order we'll
    // splice the dragged task into.
    const targetGroup = (groups.get(targetMilestoneId) ?? [])
      .filter((t) => t.id !== activeId)
      .sort((a, b) => ((a.sort_order ?? 0) - (b.sort_order ?? 0)) || (a.id - b.id));

    // Determine where the dragged task lands. Drag direction matters
    // when reordering inside the SAME phase: dragging downward past
    // a task means "insert after it," not before.
    let insertIndex: number;
    if (targetTaskId == null) {
      insertIndex = targetGroup.length;
    } else {
      const overIdx = targetGroup.findIndex((t) => t.id === targetTaskId);
      if (overIdx < 0) {
        insertIndex = targetGroup.length;
      } else if (activeTask.milestone_id === targetMilestoneId) {
        const sourceGroup = (groups.get(activeTask.milestone_id) ?? [])
          .slice()
          .sort((a, b) => ((a.sort_order ?? 0) - (b.sort_order ?? 0)) || (a.id - b.id));
        const sourceIdx = sourceGroup.findIndex((t) => t.id === activeId);
        const overSourceIdx = sourceGroup.findIndex((t) => t.id === targetTaskId);
        insertIndex = sourceIdx < overSourceIdx ? overIdx + 1 : overIdx;
      } else {
        insertIndex = overIdx;
      }
    }

    // Rewrite the target phase's sort_orders sequentially. Necessary
    // because tasks seed with sort_order=0 across the board — midpoint
    // math always resolves to 0, so the DB silently keeps the original
    // position. Sequential rewrite is N PATCHes per drop instead of 1,
    // but only the rows that actually changed get touched, and N is
    // small (<20 tasks per phase typically).
    const ORDER_STEP = 100;
    const newTargetOrder = [...targetGroup.map((t) => t.id)];
    newTargetOrder.splice(insertIndex, 0, activeId);

    newTargetOrder.forEach((id, idx) => {
      const newSortOrder = (idx + 1) * ORDER_STEP;
      const t = tasks.find((x) => x.id === id);
      if (!t) return;
      const sortChanged = (t.sort_order ?? 0) !== newSortOrder;
      const milestoneChanged = id === activeId && t.milestone_id !== targetMilestoneId;
      if (!sortChanged && !milestoneChanged) return;
      const patch: Partial<Task> = {};
      if (sortChanged) patch.sort_order = newSortOrder;
      if (milestoneChanged) patch.milestone_id = targetMilestoneId;
      updateTask.mutate({ id, ...patch });
    });
  };

  // Split rows by type. Phases use the legacy 'deliverable' default; true
  // milestones use 'milestone'. Anything else is treated as a phase to keep
  // existing data visible.
  const phases = useMemo(
    () => allRows.filter((r) => r.milestone_type !== "milestone"),
    [allRows],
  );
  const milestones = useMemo(
    () => allRows.filter((r) => r.milestone_type === "milestone"),
    [allRows],
  );

  // Tasks group by phase, not by milestone — milestones are points, not buckets.
  const groups = useMemo(() => {
    const byPhase = new Map<number | null, Task[]>();
    byPhase.set(null, []);
    for (const m of phases) byPhase.set(m.id, []);
    for (const t of tasks) {
      const key = t.milestone_id ?? null;
      if (!byPhase.has(key)) byPhase.set(null, []);
      (byPhase.get(byPhase.has(key) ? key : null) ?? []).push(t);
    }
    return byPhase;
  }, [phases, tasks]);

  // WBS-style task numbers — POSITIONAL, computed from display order:
  // tasks number 1..N across phases top to bottom (unphased tasks last),
  // subtasks are parent.1, parent.2, ... So inserting or reordering
  // renumbers automatically and the numbers always match what's on
  // screen — they're a shared way to say "task 22.4", not permanent ids.
  const taskNumbers = useMemo(() => {
    const map = new Map<number, string>();
    let n = 0;
    const keys: (number | null)[] = [...phases.map((m) => m.id), null];
    for (const key of keys) {
      const { topLevel, childrenByParent } = groupSubtasks(groups.get(key) ?? []);
      for (const task of topLevel) {
        n += 1;
        map.set(task.id, String(n));
        (childrenByParent.get(task.id) ?? []).forEach((child, i) => {
          map.set(child.id, `${n}.${i + 1}`);
        });
      }
    }
    return map;
  }, [phases, groups]);

  // Auto-seed default plan when a project's Plan tab opens empty.
  //
  // PROJECTS: clone the canonical template (ETE-124) so a new project
  //   inherits the full skeleton — 5 phase deliverables, 6 milestones,
  //   the tasks under each, and milestone dependencies. Replaces the
  //   older 3-phase-headers default (Planning / Execution / Testing
  //   and Delivery) which left every new project to be filled out by
  //   hand. The clone endpoint resets completion state, so the new
  //   plan lands as 0% / not_started across the board.
  //
  // If TEMPLATE_PROJECT_ID is ever removed/renamed, the clone returns
  // a 400 and we toast — the project just stays empty, no data loss.
  const TEMPLATE_PROJECT_ID = "ETE-124";
  const [seededPhases, setSeededPhases] = useState(false);
  useEffect(() => {
    if (milestonesQ.isLoading || phases.length !== 0 || seededPhases) return;
    if (projectId === TEMPLATE_PROJECT_ID) return; // never seed the template itself
    setSeededPhases(true);
    (async () => {
      try {
        if (parentType === "project") {
          await api.post(`/portfolio/${projectId}/clone-plan-from/${TEMPLATE_PROJECT_ID}`);
          await Promise.all([milestonesQ.refetch(), tasksQ.refetch()]);
        } else {
          const defaults = ["Planning", "Execution", "Testing and Delivery"];
          for (let i = 0; i < defaults.length; i++) {
            await createMilestone.mutateAsync({ title: defaults[i], sort_order: i });
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.detail ?? "Couldn't seed default plan",
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestonesQ.isLoading, phases.length, seededPhases, parentType, projectId]);

  // Auto-seed canonical milestones on first visit. Pull initial values from
  // the project record where the field maps directly so we consolidate (don't
  // duplicate) commitments that already exist.
  // Canonical milestone seeding has been retired — phases are now the
  // milestones. Legacy rows in `project_milestones` with
  // milestone_type='milestone' stay in the DB but are filtered out of
  // the phases list and no longer rendered. The phase seed below
  // (Planning / Execution / Testing and Delivery) still runs for plans
  // that start empty.

  const [collapsed, setCollapsed] = useState<Set<number | null>>(new Set());
  const [editingTask, setEditingTask] = useState<{ task: Task | null; milestoneId: number | null; parentTaskId?: number | null } | null>(null);

  const toggleCollapse = (id: number | null) => {
    setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // Parent task ids whose subtasks are collapsed (hidden). Default expanded.
  const [collapsedSubtasks, setCollapsedSubtasks] = useState<Set<number>>(new Set());
  const toggleSubtasks = (id: number) => {
    setCollapsedSubtasks((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleAddPhase = () => {
    const title = prompt("Phase name (e.g. Discovery, Planning, Execution):");
    if (!title?.trim()) return;
    createMilestone.mutate({ title: title.trim(), sort_order: phases.length }, {
      onSuccess: () => toast.success("Phase added"),
      onError: (err: any) => {
        const detail = err?.response?.data?.detail ?? err?.message ?? "unknown error";
        toast.error(`Couldn't add phase: ${detail}`);
      },
    });
  };

  const handleDeletePhase = (id: number) => {
    if (!confirm("Delete this phase and unlink its tasks?")) return;
    deleteMilestone.mutate(id, { onSuccess: () => toast.success("Phase deleted") });
  };

  const loading = milestonesQ.isLoading || tasksQ.isLoading;

  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div>
          <div className="text-sm font-semibold text-ink">Project Plan</div>
          <p className="mt-0.5 text-xs text-ink-muted">Phases group related tasks. Click a task to edit.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Per-plan toggle: surfaces or hides the "+ Product" affordance
              on every task row. Default off — most plans don't track
              product variants. Click flips the parent record's
              use_products field; React Query refetches and the rows
              re-render with the new state. */}
          <label className="flex items-center gap-1.5 text-[11px] font-medium text-ink-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useProductsFlag}
              onChange={toggleUseProducts}
              className="h-3.5 w-3.5 rounded border-line-strong text-brand-navy-600 dark:text-brand-navy-400 focus:ring-brand-navy-400"
            />
            Track products
          </label>
          <button onClick={handleAddPhase} disabled={createMilestone.isPending}
            className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors">
            <Plus className="h-3 w-3" /> Add phase
          </button>
          <button onClick={() => setEditingTask({ task: null, milestoneId: null })}
            className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 transition-colors">
            <Plus className="h-3 w-3" /> Add task
          </button>
        </div>
      </div>

      {loading && <div className="px-5 py-12 text-center text-sm text-ink-muted">Loading plan...</div>}

      {/* Milestones section is intentionally not rendered anymore —
          phases ARE milestones now (date, completion, blockers all live
          on the phase header). Legacy canonical milestone rows from
          older projects stay in the DB but are filtered out of the
          phases list via the `milestone_type !== "milestone"` predicate. */}

      {!loading && phases.length === 0 && tasks.length === 0 && (
        <div className="px-5 py-12 text-center text-sm text-ink-muted">
          No phases or tasks yet. Click <strong>Add phase</strong> to create your first one.
        </div>
      )}

      {!loading && (phases.length > 0 || tasks.length > 0) && (
        <DndContext
          sensors={sensors}
          // closestCenter picks the droppable with the nearest center —
          // which is the specific task row under the cursor, not the
          // much-larger phase wrapper. Without this, every drop was
          // being attributed to the phase wrapper (treated as "drop on
          // empty header") and tasks snapped back to their original
          // place because they were just being re-appended to the same
          // phase's end with no positional change.
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDragId(null)}
        >
        <div className="divide-y divide-line-soft">
          {phases.map((m) => (
            <PhaseSection key={m.id} milestone={m} tasks={groups.get(m.id) ?? []}
              numbering={taskNumbers}
              collapsed={collapsed.has(m.id)} onToggle={() => toggleCollapse(m.id)}
              onAddTask={() => setEditingTask({ task: null, milestoneId: m.id })}
              onAddSubtask={(parent) => setEditingTask({ task: null, milestoneId: parent.milestone_id, parentTaskId: parent.id })}
              collapsedSubtasks={collapsedSubtasks}
              onToggleSubtasks={toggleSubtasks}
              onEditTask={(t) => setEditingTask({ task: t, milestoneId: t.milestone_id })}
              onCompleteTask={(id) => completeTask.mutate(id)}
              onPatchTask={(id, patch) => updateTask.mutate({ id, ...patch })}
              onRenamePhase={(title) => updateMilestone.mutate({ id: m.id, title })}
              onPatchPhase={(patch) => updateMilestone.mutate({ id: m.id, ...patch })}
              onAddBlocker={(blockerMilestoneId) => addBlocker.mutate({ milestoneId: m.id, blockerMilestoneId })}
              onRemoveBlocker={(blockerMilestoneId) => removeBlocker.mutate({ milestoneId: m.id, blockerMilestoneId })}
              onDeletePhase={() => handleDeletePhase(m.id)}
              parentType={parentType}
              productSuggestions={productSuggestions}
              productsEnabled={useProductsFlag} />
          ))}
          {(groups.get(null)?.length ?? 0) > 0 && (
            <PhaseSection milestone={null} tasks={groups.get(null) ?? []}
              numbering={taskNumbers}
              collapsed={collapsed.has(null)} onToggle={() => toggleCollapse(null)}
              onAddTask={() => setEditingTask({ task: null, milestoneId: null })}
              onAddSubtask={(parent) => setEditingTask({ task: null, milestoneId: parent.milestone_id, parentTaskId: parent.id })}
              collapsedSubtasks={collapsedSubtasks}
              onToggleSubtasks={toggleSubtasks}
              onEditTask={(t) => setEditingTask({ task: t, milestoneId: t.milestone_id })}
              onCompleteTask={(id) => completeTask.mutate(id)}
              onPatchTask={(id, patch) => updateTask.mutate({ id, ...patch })}
              parentType={parentType}
              productSuggestions={productSuggestions}
              productsEnabled={useProductsFlag} />
          )}
        </div>
        <DragOverlay dropAnimation={{ duration: 180 }}>
          {activeDragTask ? (
            <div className="flex items-center gap-2 rounded-lg border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-4 py-2 shadow-2xl ring-2 ring-brand-navy-200 dark:ring-brand-navy-500/30">
              <GripVertical className="h-4 w-4 text-brand-navy-400" />
              <span className="text-sm font-medium text-ink">{activeDragTask.title}</span>
            </div>
          ) : null}
        </DragOverlay>
        </DndContext>
      )}

      {/* Edit/Create Task Modal */}
      {editingTask !== null && (
        <TaskModal
          task={
            // Re-resolve from the live tasks list so the Blocked-by
            // section reflects edges added/removed while the modal is open
            // (the editingTask snapshot goes stale on refetch).
            editingTask.task
              ? tasks.find((t) => t.id === editingTask.task!.id) ?? editingTask.task
              : null
          }
          milestones={phases}
          defaultMilestoneId={editingTask.milestoneId}
          allTasks={tasks}
          onAddBlocker={(taskId, blockerTaskId) => addTaskBlocker.mutate({ taskId, blockerTaskId })}
          onRemoveBlocker={(taskId, blockerTaskId) => removeTaskBlocker.mutate({ taskId, blockerTaskId })}
          onClose={() => setEditingTask(null)}
          onSave={(fields) => {
            if (editingTask.task) {
              updateTask.mutate({ id: editingTask.task.id, ...fields } as any, {
                onSuccess: () => { setEditingTask(null); toast.success("Task updated"); },
              });
            } else {
              const parentId = editingTask.parentTaskId ?? null;
              createTask.mutate({ ...fields, parent_task_id: parentId } as any, {
                onSuccess: () => { setEditingTask(null); toast.success(parentId ? "Subtask created" : "Task created"); },
              });
            }
          }}
          onDelete={editingTask.task ? () => {
            deleteTask.mutate(editingTask.task!.id, {
              onSuccess: () => { setEditingTask(null); toast.success("Task deleted"); },
            });
          } : undefined}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function computeRollup(tasks: Task[]) {
  let completedCount = 0, hours = 0, completedHours = 0;
  for (const t of tasks) {
    if (t.status === "complete") completedCount++;
    hours += t.est_hours ?? 0;
    if (t.status === "complete") completedHours += t.est_hours ?? 0;
  }
  const pct = hours > 0 ? completedHours / hours : tasks.length > 0 ? completedCount / tasks.length : 0;
  return { count: tasks.length, completedCount, hours, pct };
}

/** Derive a phase's date range from the min/max of its task dates. */
function deriveRange(tasks: Task[]): { start: string | null; end: string | null } {
  let minStart: number | null = null;
  let maxEnd: number | null = null;
  for (const t of tasks) {
    if (t.start_date) {
      const ms = new Date(t.start_date).getTime();
      if (!Number.isNaN(ms) && (minStart === null || ms < minStart)) minStart = ms;
    }
    if (t.end_date) {
      const ms = new Date(t.end_date).getTime();
      if (!Number.isNaN(ms) && (maxEnd === null || ms > maxEnd)) maxEnd = ms;
    }
  }
  return {
    start: minStart !== null ? new Date(minStart).toISOString().slice(0, 10) : null,
    end: maxEnd !== null ? new Date(maxEnd).toISOString().slice(0, 10) : null,
  };
}

// ---------------------------------------------------------------------------
// Milestones — fixed canonical list, inline editing, no add/delete in v1.
// ---------------------------------------------------------------------------
function MilestonesSection({ milestones, onPatch, parentType, onAddBlocker, onRemoveBlocker }: {
  milestones: Milestone[];
  onPatch: (id: number, patch: Partial<Milestone>) => void;
  parentType: ParentType;
  onAddBlocker: (milestoneId: number, blockerMilestoneId: number) => void;
  onRemoveBlocker: (milestoneId: number, blockerMilestoneId: number) => void;
}) {
  // Sort by sort_order (canonical order set at seed time).
  const ordered = useMemo(() => [...milestones].sort((a, b) => a.sort_order - b.sort_order), [milestones]);

  const setCount = ordered.filter((m) => !!m.due_date).length;
  const doneCount = ordered.filter((m) => !!m.completed_date || m.status === "complete").length;

  return (
    <div className="border-b border-line bg-surface">
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-b from-slate-50 to-surface">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-sm font-semibold text-ink">Milestones</div>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Key dates for this project. Shown as diamonds on the Timeline.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-ink-muted">
          <span className="rounded-full bg-surface px-2 py-0.5 font-bold tabular-nums text-ink-2 ring-1 ring-line">
            {doneCount}/{ordered.length} done
          </span>
          <span className="rounded-full bg-surface px-2 py-0.5 tabular-nums ring-1 ring-line">
            {setCount}/{ordered.length} dated
          </span>
        </div>
      </div>

      <div className="divide-y divide-line-soft">
        {ordered.map((m) => (
          <MilestoneRow
            key={m.id}
            milestone={m}
            parentType={parentType}
            onDateChange={(due) => onPatch(m.id, { due_date: due })}
            onToggleDone={(done) => onPatch(m.id, done
              ? { status: "complete", completed_date: new Date().toISOString().slice(0, 10) }
              : { status: "not_started", completed_date: null }
            )}
            onAddBlocker={(blockerId) => onAddBlocker(m.id, blockerId)}
            onRemoveBlocker={(blockerId) => onRemoveBlocker(m.id, blockerId)}
          />
        ))}
      </div>
    </div>
  );
}

function MilestoneRow({ milestone, parentType, onDateChange, onToggleDone, onAddBlocker, onRemoveBlocker }: {
  milestone: Milestone;
  parentType: ParentType;
  onDateChange: (due: string | null) => void;
  onToggleDone: (done: boolean) => void;
  onAddBlocker: (blockerMilestoneId: number) => void;
  onRemoveBlocker: (blockerMilestoneId: number) => void;
}) {
  const isDone = !!milestone.completed_date || milestone.status === "complete";
  const tone = dateColor(milestone.due_date, milestone.completed_date);
  const [pendingDate, setPendingDate] = useState(milestone.due_date ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const blockers = milestone.blockers ?? [];

  return (
    <div className="group hover:bg-raised/60">
      <div className="grid grid-cols-12 items-center gap-3 px-5 py-2 text-sm">
        <div className="col-span-5 flex items-center gap-2.5">
          {/* Date-derived tone diamond — milestone's at-a-glance health signal. */}
          <svg width={14} height={14} viewBox="0 0 14 14" className="shrink-0 drop-shadow-sm" aria-hidden>
            <polygon points="7,1 13,7 7,13 1,7" className={cn(tone.fill, tone.ring)} strokeWidth={1.5} />
          </svg>
          <span className={cn("font-medium text-ink", isDone && "line-through text-ink-muted")}>
            {milestone.title}
          </span>
        </div>

        <div className="col-span-4 flex items-center gap-2">
          <input
            type="date"
            value={pendingDate}
            onChange={(e) => {
              const v = e.target.value;
              setPendingDate(v);
              onDateChange(v || null);
              toast.success(v ? `${milestone.title} set to ${shortDate(v)}` : `${milestone.title} date cleared`);
            }}
            className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-2 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
          />
          <span className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-semibold",
            tone.label === "Overdue"  && "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
            tone.label === "Due soon" && "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
            tone.label === "Upcoming" && "bg-raised text-ink-3",
            tone.label === "Complete" && "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
            tone.label === "Not set"  && "bg-canvas text-ink-subtle",
          )}>
            {tone.label}
          </span>
        </div>

        <div className="col-span-3 flex items-center justify-end">
          <label className="inline-flex items-center gap-2 text-xs text-ink-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isDone}
              onChange={(e) => {
                onToggleDone(e.target.checked);
                toast.success(e.target.checked ? `${milestone.title} marked complete` : `${milestone.title} reopened`);
              }}
              className="h-4 w-4 rounded border-line-strong text-emerald-600 dark:text-emerald-400 focus:ring-emerald-400"
            />
            Done
          </label>
        </div>
      </div>

      {/* Blockers strip — always visible below each milestone row so the
          "+ Add blocker" affordance is discoverable. Each blocker chip
          is colored by its own status: red if overdue and not complete,
          amber if due soon, slate otherwise, emerald if complete. */}
      <div className="flex flex-wrap items-center gap-1.5 px-5 pb-2 pl-9">
        {blockers.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
            <Lock className="h-2.5 w-2.5" /> Blocked by
          </span>
        )}
        {blockers.map((b) => (
          <BlockerChip
            key={b.dep_id}
            blocker={b}
            onRemove={() => onRemoveBlocker(b.id)}
          />
        ))}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-line-strong px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-brand-navy-400 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
        >
          <Plus className="h-2.5 w-2.5" />
          {blockers.length === 0 ? "Add blocker" : "Add"}
        </button>
      </div>

      {pickerOpen && (
        <BlockerPicker
          milestone={milestone}
          parentType={parentType}
          existingBlockerIds={new Set(blockers.map((b) => b.id))}
          onPick={(blockerId) => {
            onAddBlocker(blockerId);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BlockerChip — one upstream-milestone pill. Colored by the blocker's
// own derived status so the eye lands on the red ones first (overdue +
// not complete = the blockers that are actually holding things up).
// ---------------------------------------------------------------------------
function BlockerChip({ blocker, onRemove }: {
  blocker: BlockerRef;
  onRemove: () => void;
}) {
  const isComplete = !!blocker.completed_date || blocker.status === "complete";
  const tone = dateColor(blocker.due_date, blocker.completed_date);
  const slipDays =
    !isComplete && blocker.due_date
      ? Math.floor(
          (Date.now() - new Date(blocker.due_date).setHours(0, 0, 0, 0)) /
            86400000,
        )
      : 0;
  const chipClass = isComplete
    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30"
    : tone.label === "Overdue"
    ? "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-500/30"
    : tone.label === "Due soon"
    ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30"
    : "bg-canvas text-ink-3 ring-line";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium ring-1",
        chipClass,
      )}
      title={`${blocker.parent_name ?? blocker.parent_id} · ${blocker.title}${
        blocker.due_date ? ` · due ${shortDate(blocker.due_date)}` : ""
      }`}
    >
      <span className="font-semibold">{blocker.parent_id}</span>
      <span className="text-ink-subtle">·</span>
      <span className="truncate max-w-[160px]">{blocker.title}</span>
      {!isComplete && slipDays > 0 && (
        <span className="font-bold tabular-nums">+{slipDays}d</span>
      )}
      {isComplete && <CheckCircle2 className="h-2.5 w-2.5" />}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove blocker"
        className="opacity-50 hover:opacity-100 transition-opacity"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// BlockerPicker — modal listing every milestone of the matching parent
// type. Search by title or parent name. Excludes the current milestone + its existing blockers.
// Cross-type and cycle detection are intentionally out of scope for v1.
// ---------------------------------------------------------------------------
function BlockerPicker({ milestone, parentType, existingBlockerIds, onPick, onClose }: {
  milestone: Milestone;
  parentType: ParentType;
  existingBlockerIds: Set<number>;
  onPick: (blockerId: number) => void;
  onClose: () => void;
}) {
  const all = useAllMilestones(parentType);
  const [query, setQuery] = useState("");
  const candidates = useMemo(() => {
    const list = all.data ?? [];
    const q = query.trim().toLowerCase();
    return list
      .filter((m) => m.id !== milestone.id && !existingBlockerIds.has(m.id))
      .filter((m) => {
        if (!q) return true;
        const inTitle = m.title.toLowerCase().includes(q);
        const inParent = (m.parent_name ?? "").toLowerCase().includes(q);
        return inTitle || inParent;
      })
      .slice(0, 100);
  }, [all.data, query, milestone.id, existingBlockerIds]);

  // Esc closes — keeps the picker as dismissible as the rest of the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0"
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl bg-surface shadow-2xl ring-1 ring-line">
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink">
              What's blocking "{milestone.title}"?
            </h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-ink-subtle hover:bg-raised">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-line-soft px-5 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${parentType} milestones…`}
              className="w-full rounded-md border border-line bg-surface py-1.5 pl-8 pr-3 text-xs text-ink-2 placeholder:text-ink-subtle focus:border-brand-navy-300 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {all.isLoading && (
            <div className="px-5 py-6 text-center text-xs text-ink-subtle">Loading…</div>
          )}
          {!all.isLoading && candidates.length === 0 && (
            <div className="px-5 py-6 text-center text-xs italic text-ink-subtle">
              {query ? "No matches" : "No other milestones available"}
            </div>
          )}
          {candidates.map((c) => {
            const cTone = dateColor(c.due_date, c.completed_date);
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c.id)}
                className="flex w-full items-center justify-between gap-3 border-t border-line-soft px-5 py-2 text-left text-xs first:border-t-0 hover:bg-raised"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-ink">{c.title}</div>
                  <div className="mt-0.5 truncate text-[10px] text-ink-muted">
                    <span className="font-mono">{c.project_id ?? c.initiative_id}</span>
                    {c.parent_name ? ` · ${c.parent_name}` : ""}
                  </div>
                </div>
                <span className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                  cTone.label === "Overdue" && "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
                  cTone.label === "Due soon" && "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
                  cTone.label === "Upcoming" && "bg-raised text-ink-3",
                  cTone.label === "Complete" && "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
                  cTone.label === "Not set" && "bg-canvas text-ink-subtle",
                )}>
                  {cTone.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PhaseSection({ milestone, tasks, collapsed, parentType, onToggle, onAddTask, onAddSubtask, collapsedSubtasks, onToggleSubtasks, onEditTask, onCompleteTask, onPatchTask, onPatchPhase, onRenamePhase, onDeletePhase, onAddBlocker, onRemoveBlocker, productSuggestions, productsEnabled, numbering }: {
  milestone: Milestone | null; tasks: Task[]; collapsed: boolean;
  numbering?: Map<number, string>;
  parentType: ParentType;
  onToggle: () => void; onAddTask: () => void; onAddSubtask?: (parent: Task) => void; onEditTask: (t: Task) => void;
  collapsedSubtasks?: ReadonlySet<number>; onToggleSubtasks?: (id: number) => void;
  onCompleteTask: (id: number) => void;
  onPatchTask: (id: number, patch: Partial<Task>) => void;
  /** Patch the phase row itself — date, completion, status, etc. Phases
   *  ARE milestones now; this is the same shape as a milestone update. */
  onPatchPhase?: (patch: Partial<Milestone>) => void;
  onRenamePhase?: (title: string) => void;
  onDeletePhase?: () => void;
  onAddBlocker?: (blockerMilestoneId: number) => void;
  onRemoveBlocker?: (blockerMilestoneId: number) => void;
  productSuggestions: string[];
  productsEnabled: boolean;
}) {
  const title = milestone?.title ?? "Unassigned";
  const rollup = useMemo(() => computeRollup(tasks), [tasks]);
  const range = useMemo(() => deriveRange(tasks), [tasks]);
  // Subtask grouping: per-parent child counts (for the caret + count badge)
  // and the collapse-aware render order (parents, then their visible children).
  const childrenByParent = useMemo(() => groupSubtasks(tasks).childrenByParent, [tasks]);
  const visibleRows = useMemo(
    () => orderWithSubtasks(tasks, collapsedSubtasks),
    [tasks, collapsedSubtasks],
  );
  const isDone = !!milestone?.completed_date || milestone?.status === "complete";
  // Date-derived tone diamond — phases now carry the same at-a-glance
  // RYG signal milestones used to. Red if overdue + not complete, amber
  // if due soon, emerald if complete, slate otherwise.
  const phaseTone = milestone ? dateColor(milestone.due_date, milestone.completed_date) : null;
  const blockers = milestone?.blockers ?? [];
  // Inline rename state — title becomes an input when editing.
  // Esc cancels (no save), Enter / blur commits if changed and non-empty.
  const [renaming, setRenaming] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingDate, setPendingDate] = useState(milestone?.due_date ?? "");
  const commitRename = () => {
    const next = draftTitle.trim();
    if (next && next !== title && onRenamePhase) onRenamePhase(next);
    setRenaming(false);
  };

  // Droppable target for cross-phase drops onto an empty phase (or the
  // phase header). SortableContext alone doesn't register drops on an
  // empty list, so we wrap the whole phase in a useDroppable with a
  // phase- sentinel id that handleDragEnd recognizes.
  const droppableId = `phase-${milestone?.id ?? "null"}`;
  const { setNodeRef: setPhaseDropRef, isOver: isPhaseDropOver } = useDroppable({ id: droppableId });

  return (
    <div
      ref={setPhaseDropRef}
      className={cn(isPhaseDropOver && "ring-2 ring-inset ring-brand-navy-300 bg-brand-navy-50/40 dark:bg-brand-navy-500/10")}
    >
      <div className={cn("flex items-center gap-3 px-5 py-3", milestone ? "bg-canvas" : "bg-canvas/50")}>
        <button onClick={onToggle} className="text-ink-subtle hover:text-ink-3">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {/* RYG diamond — phases now carry milestone visual. Hidden for
            the "Unassigned" bucket (no milestone row backing it). */}
        {milestone && phaseTone && (
          <svg width={14} height={14} viewBox="0 0 14 14" className="shrink-0 drop-shadow-sm" aria-hidden>
            <polygon points="7,1 13,7 7,13 1,7" className={cn(phaseTone.fill, phaseTone.ring)} strokeWidth={1.5} />
          </svg>
        )}
        {renaming && onRenamePhase ? (
          <input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitRename();
              else if (e.key === "Escape") { setDraftTitle(title); setRenaming(false); }
            }}
            className="rounded-md border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-0.5 text-sm font-semibold text-ink focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
          />
        ) : (
          <button
            type="button"
            onClick={() => { if (onRenamePhase) { setDraftTitle(title); setRenaming(true); } }}
            disabled={!onRenamePhase}
            title={onRenamePhase ? "Click to rename phase" : undefined}
            className={cn(
              "text-sm font-semibold",
              !milestone && "italic text-ink-muted",
              isDone && milestone && "line-through text-ink-muted",
              onRenamePhase && "rounded px-1 -mx-1 hover:bg-raised transition-colors cursor-text",
            )}
          >
            {title}
          </button>
        )}
        {/* Phase due date — editable inline. Replaces the read-only "Due
            {date}" text we used to show. Falls back to task-derived range
            below the header when due_date is empty. */}
        {milestone && onPatchPhase && (
          <input
            type="date"
            value={pendingDate}
            onChange={(e) => {
              const v = e.target.value;
              setPendingDate(v);
              onPatchPhase({ due_date: v || null });
            }}
            className="rounded-md border border-line bg-surface px-2 py-1 text-[11px] text-ink-2 focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
            title="Phase target completion date"
          />
        )}
        {milestone && phaseTone && phaseTone.label !== "Not set" && (
          <span className={cn(
            "rounded-full px-2 py-0.5 text-[10px] font-semibold",
            phaseTone.label === "Overdue"  && "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
            phaseTone.label === "Due soon" && "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
            phaseTone.label === "Upcoming" && "bg-raised text-ink-3",
            phaseTone.label === "Complete" && "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
          )}>
            {phaseTone.label}
          </span>
        )}
        {/* Task-derived range only shown when due_date is unset — purely
            informational fallback so the header reads as "we have task
            dates even without an explicit phase deadline." */}
        {milestone && !milestone.due_date && range.start && range.end && (
          <span className="text-[10px] italic text-ink-subtle" title="Derived from task dates">
            {shortDate(range.start)} → {shortDate(range.end)}
          </span>
        )}
        <div className="flex items-center gap-3 text-[11px] text-ink-muted">
          <span className="rounded-full bg-surface px-2 py-0.5 font-bold tabular-nums text-ink-2 ring-1 ring-line">
            {rollup.count} {rollup.count === 1 ? "task" : "tasks"}
          </span>
          <span className="tabular-nums">{rollup.hours.toFixed(0)}h</span>
          {rollup.count > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                <div className={cn("h-full rounded-full", rollup.pct >= 1 ? "bg-emerald-500" : "bg-sky-500")}
                  style={{ width: `${Math.min(rollup.pct * 100, 100)}%` }} />
              </div>
              <span className="tabular-nums font-semibold text-ink-2">{Math.round(rollup.pct * 100)}%</span>
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Done toggle — phases ARE milestones now, so a phase can be
              "completed" the same way the old canonical milestones could.
              Sets completed_date + status, or clears both on reopen. */}
          {milestone && onPatchPhase && (
            <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-ink-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isDone}
                onChange={(e) => {
                  if (e.target.checked) {
                    onPatchPhase({
                      status: "complete",
                      completed_date: new Date().toISOString().slice(0, 10),
                    });
                  } else {
                    onPatchPhase({ status: "not_started", completed_date: null });
                  }
                }}
                className="h-3.5 w-3.5 rounded border-line-strong text-emerald-600 dark:text-emerald-400 focus:ring-emerald-400"
              />
              Done
            </label>
          )}
          <button onClick={onAddTask} className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-3 transition-colors" title="Add task">
            <Plus className="h-3.5 w-3.5" />
          </button>
          {onDeletePhase && (
            <button onClick={onDeletePhase} className="rounded p-1 text-ink-faint hover:bg-red-50 dark:hover:bg-red-500/15 hover:text-red-500 transition-colors" title="Delete phase">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Blockers strip — phases now carry dependencies (we collapsed
          phases and milestones into one concept). Same UI the milestone
          rows had: colored chips per blocker, "+ Add blocker" affordance. */}
      {milestone && onAddBlocker && onRemoveBlocker && (
        <div className={cn(
          "flex flex-wrap items-center gap-1.5 px-5 pb-2 pl-12",
          milestone ? "bg-canvas" : "bg-canvas/50",
        )}>
          {blockers.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
              <Lock className="h-2.5 w-2.5" /> Blocked by
            </span>
          )}
          {blockers.map((b) => (
            <BlockerChip
              key={b.dep_id}
              blocker={b}
              onRemove={() => onRemoveBlocker(b.id)}
            />
          ))}
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-line-strong bg-surface px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-brand-navy-400 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 transition-colors"
          >
            <Plus className="h-2.5 w-2.5" />
            {blockers.length === 0 ? "Add blocker" : "Add"}
          </button>
        </div>
      )}

      {pickerOpen && milestone && onAddBlocker && (
        <BlockerPicker
          milestone={milestone}
          parentType={parentType}
          existingBlockerIds={new Set(blockers.map((b) => b.id))}
          onPick={(blockerId) => {
            onAddBlocker(blockerId);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {!collapsed && (
        tasks.length === 0 ? (
          <div className="px-12 py-3 text-xs italic text-ink-subtle">No tasks yet. Drop a task here, or click + to add one.</div>
        ) : (
          <>
            <div className="grid grid-cols-12 gap-3 border-t border-line-soft bg-canvas/30 px-5 py-1.5 pl-12 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
              <div className="col-span-4">Task</div>
              <div className="col-span-2">Assignee</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Priority</div>
              <div className="col-span-2">Dates</div>
              <div className="col-span-1 text-right">Hours</div>
            </div>
            <SortableContext
              items={visibleRows.map((o) => o.task.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleRows.map(({ task: t, depth }) => (
                <SortableTaskRow
                  key={t.id}
                  task={t}
                  number={numbering?.get(t.id)}
                  depth={depth}
                  subtaskCount={depth === 0 ? (childrenByParent.get(t.id)?.length ?? 0) : 0}
                  collapsed={collapsedSubtasks?.has(t.id) ?? false}
                  onToggleSubtasks={onToggleSubtasks}
                  onAddSubtask={onAddSubtask}
                  onEditTask={onEditTask}
                  onCompleteTask={onCompleteTask}
                  onPatchTask={onPatchTask}
                  productSuggestions={productSuggestions}
                  productsEnabled={productsEnabled}
                />
              ))}
            </SortableContext>
          </>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortableTaskRow — one task row that can be picked up and reordered or
// moved across phases. The grip handle on the left is the drag activator;
// the rest of the row remains clickable to open the edit modal.
// ---------------------------------------------------------------------------
function SortableTaskRow({ task: t, number, depth = 0, subtaskCount = 0, collapsed = false, onToggleSubtasks, onAddSubtask, onEditTask, onCompleteTask, onPatchTask, productSuggestions, productsEnabled }: {
  number?: string;
  task: Task;
  depth?: 0 | 1;
  subtaskCount?: number;
  collapsed?: boolean;
  onToggleSubtasks?: (id: number) => void;
  onAddSubtask?: (parent: Task) => void;
  onEditTask: (t: Task) => void;
  onCompleteTask: (id: number) => void;
  onPatchTask: (id: number, patch: Partial<Task>) => void;
  productSuggestions: string[];
  productsEnabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: t.id });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };
  const status = t.status ?? "not_started";
  const StatusIcon = STATUS_ICON[status] ?? Circle;
  const isComplete = status === "complete";
  const dateTone = dateColor(t.end_date, isComplete ? (t.updated_at ?? new Date().toISOString()) : null);
  const showDateBadge = !isComplete && (dateTone.label === "Overdue" || dateTone.label === "Due soon");
  const taskHealth = deriveTaskHealth(t);
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onEditTask(t)}
      className={cn(
        "group relative grid cursor-pointer grid-cols-12 items-center gap-3 border-t border-line-soft bg-surface px-5 py-2 pl-7 text-sm hover:bg-raised",
        isDragging && "opacity-30",
        // When another task is being dragged over this row, draw a thick
        // indigo line on the top edge so the drop position is unmissable.
        // The sortable strategy already shifts neighbor rows visually,
        // but the line removes any ambiguity about WHERE between rows
        // the drop will land.
        isOver && !isDragging && "before:absolute before:left-0 before:right-0 before:-top-px before:h-0.5 before:bg-brand-navy-500 before:rounded-full",
      )}
    >
      <div className="col-span-4 flex items-center gap-2 min-w-0" style={depth ? { paddingLeft: depth * 22 } : undefined}>
        {depth > 0 && (
          <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
        )}
        {depth === 0 && subtaskCount > 0 && onToggleSubtasks && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSubtasks(t.id); }}
            aria-label={collapsed ? "Show subtasks" : "Hide subtasks"}
            className="shrink-0 text-ink-subtle hover:text-ink-2"
          >
            {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        )}
        {/* Drag handle — only this element is the activator, so clicks
            elsewhere on the row still open the edit modal */}
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to reorder"
          className="flex h-5 w-4 cursor-grab items-center justify-center text-ink-faint hover:text-ink-3 active:cursor-grabbing"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button onClick={(e) => { e.stopPropagation(); if (!isComplete) onCompleteTask(t.id); }}
          disabled={isComplete} className={cn("shrink-0 text-ink-faint hover:text-emerald-500 transition-colors", isComplete && "text-emerald-500 cursor-default")}>
          <StatusIcon className="h-4 w-4" />
        </button>
        <HealthDot health={taskHealth} />
        {number && (
          <span className="shrink-0 font-mono text-[11px] font-semibold tabular-nums text-ink-subtle">
            {number}
          </span>
        )}
        <span className={cn("truncate font-medium text-ink", isComplete && "line-through text-ink-muted")}>{t.title}</span>
        {subtaskCount > 0 && (
          <span
            className="shrink-0 rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-medium text-ink-muted"
            title={`${subtaskCount} subtask${subtaskCount > 1 ? "s" : ""}`}
          >
            {subtaskCount}
          </span>
        )}
        <BlockedByBadge blockers={t.blockers ?? []} />
        {productsEnabled && (
          <ProductPills
            products={t.products ?? []}
            suggestions={productSuggestions}
            onChange={(products) => onPatchTask(t.id, { products })}
          />
        )}
      </div>
      <div className="col-span-2 truncate text-xs text-ink-3">{t.assignee ?? "—"}</div>
      <div className="col-span-2">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", STATUS_BG[status])}>
          {STATUS_LABEL[status] ?? status}
        </span>
      </div>
      <div className="col-span-1">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", PRIORITY_BG[t.priority] ?? PRIORITY_BG.Medium)}>
          {t.priority ?? "—"}
        </span>
      </div>
      <div className="col-span-2 flex items-center gap-1.5 text-[11px] text-ink-muted tabular-nums">
        <span>{t.start_date || t.end_date ? `${t.start_date ? shortDate(t.start_date) : "—"} → ${t.end_date ? shortDate(t.end_date) : "—"}` : "—"}</span>
        {showDateBadge && (
          <span className={cn(
            "rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
            dateTone.label === "Overdue"  && "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300",
            dateTone.label === "Due soon" && "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300",
          )}>
            {dateTone.label}
          </span>
        )}
      </div>
      <div className="col-span-1 flex items-center justify-end gap-2 text-xs tabular-nums text-ink-3">
        {depth === 0 && onAddSubtask && (
          <button
            onClick={(e) => { e.stopPropagation(); onAddSubtask(t); }}
            title="Add subtask"
            aria-label="Add subtask"
            className="flex shrink-0 items-center gap-0.5 rounded px-1 text-[11px] font-medium text-ink-subtle hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
          >
            <Plus className="h-3.5 w-3.5" /> sub
          </button>
        )}
        <span>{(t.est_hours ?? 0).toFixed(0)}h</span>
        <Pencil className="h-3 w-3 text-ink-faint opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProductPills — inline tag chips on a task row, with a "+" affordance
// that opens a small popover for adding more.
//
// The project's product list is emergent — passed in as `suggestions`,
// computed at the PlanPanel level from the union of tags across all
// tasks. Typing a new name and pressing Enter creates the tag on this
// task; once persisted the new name shows up in suggestions for other
// tasks via React Query refetch.
//
// Each interaction is one PATCH on the task (sends the full new
// products array, not a delta).
// ---------------------------------------------------------------------------
function ProductPills({ products, suggestions, onChange }: {
  products: string[];
  suggestions: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. Stops the row's onClick from also firing.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
        setDraft("");
      }
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const addProduct = (name: string) => {
    const v = name.trim();
    if (!v) return;
    if (products.some((p) => p.toLowerCase() === v.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...products, v]);
    setDraft("");
  };

  const removeProduct = (name: string) => {
    onChange(products.filter((p) => p !== name));
  };

  // Filter suggestions: hide ones already on this task, fuzzy match the draft.
  const filteredSuggestions = useMemo(() => {
    const onTask = new Set(products.map((p) => p.toLowerCase()));
    const needle = draft.trim().toLowerCase();
    return suggestions
      .filter((s) => !onTask.has(s.toLowerCase()))
      .filter((s) => !needle || s.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [suggestions, products, draft]);

  const showCreateOption =
    draft.trim().length > 0 &&
    !suggestions.some((s) => s.toLowerCase() === draft.trim().toLowerCase()) &&
    !products.some((p) => p.toLowerCase() === draft.trim().toLowerCase());

  return (
    <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
      {products.map((p) => {
        const color = productColor(p);
        return (
          <span
            key={p}
            className={cn(
              "inline-flex items-center gap-0.5 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold",
              color.bg, color.text, color.border,
            )}
          >
            {p}
            <button
              type="button"
              onClick={() => removeProduct(p)}
              aria-label={`Remove ${p}`}
              className="ml-0.5 opacity-50 hover:opacity-100 transition-opacity"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        );
      })}
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label="Add product"
          title="Add product"
          className={cn(
            "flex items-center gap-1 rounded-md border border-dashed text-[10px] font-semibold transition-colors",
            // Empty state: show "+ Product" with text so the affordance
            // is discoverable. Once at least one pill exists, collapse
            // to just "+" so the row stays compact.
            products.length === 0
              ? "border-line-strong px-1.5 py-0.5 text-ink-muted hover:border-brand-navy-400 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
              : "h-5 w-5 justify-center border-line-strong text-ink-subtle hover:border-brand-navy-300 hover:text-brand-navy-600 dark:hover:text-brand-navy-400",
          )}
        >
          <Plus className="h-3 w-3" />
          {products.length === 0 && <span>Product</span>}
        </button>
        {open && (
          <div
            ref={popoverRef}
            className="absolute left-0 top-6 z-30 w-56 rounded-md border border-line bg-surface shadow-lg ring-1 ring-line/50"
          >
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (filteredSuggestions[0] && !showCreateOption) {
                    addProduct(filteredSuggestions[0]);
                  } else {
                    addProduct(draft);
                  }
                } else if (e.key === "Escape") {
                  setOpen(false);
                  setDraft("");
                }
              }}
              placeholder="Add product…"
              className="block w-full rounded-t-md border-b border-line-soft px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
            />
            <div className="max-h-48 overflow-y-auto py-1">
              {filteredSuggestions.map((s) => {
                const color = productColor(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => addProduct(s)}
                    className="flex w-full items-center gap-2 px-2.5 py-1 text-left text-xs hover:bg-raised"
                  >
                    <span className={cn("inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold", color.bg, color.text, color.border)}>
                      {s}
                    </span>
                  </button>
                );
              })}
              {showCreateOption && (
                <button
                  type="button"
                  onClick={() => addProduct(draft)}
                  className="flex w-full items-center gap-1 px-2.5 py-1 text-left text-xs text-brand-navy-700 dark:text-brand-navy-300 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15"
                >
                  <Plus className="h-3 w-3" />
                  Create "<span className="font-semibold">{draft.trim()}</span>"
                </button>
              )}
              {!showCreateOption && filteredSuggestions.length === 0 && (
                <div className="px-2.5 py-1 text-[11px] italic text-ink-subtle">
                  Type a name and press Enter to create the first product.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task dependencies — "blocked by" between tasks in the same plan.
// Visualize-only: chips show what's holding a task up; removing a chip is
// the only state change. Managed inside the Task Modal; surfaced on the
// row via BlockedByBadge.
// ---------------------------------------------------------------------------
const isBlockerDone = (b: TaskBlockerRef) =>
  (b.status ?? "").toLowerCase() === "complete";

// Compact row badge — only rendered when a task has blockers. Red while
// any upstream task is still open; green once they've all completed.
function BlockedByBadge({ blockers }: { blockers: TaskBlockerRef[] }) {
  if (blockers.length === 0) return null;
  const openCount = blockers.filter((b) => !isBlockerDone(b)).length;
  const tone =
    openCount > 0
      ? "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-500/30"
      : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30";
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ring-1",
        tone,
      )}
      title={`Blocked by: ${blockers.map((b) => b.title).join(", ")}`}
    >
      <Lock className="h-2.5 w-2.5" />
      {openCount > 0 ? `Blocked by ${openCount}` : "Blockers cleared"}
    </span>
  );
}

// One upstream-task pill inside the Task Modal's "Blocked by" section.
function TaskBlockerChip({ blocker, onRemove }: {
  blocker: TaskBlockerRef;
  onRemove: () => void;
}) {
  const done = isBlockerDone(blocker);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1",
        done
          ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30"
          : "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-500/30",
      )}
      title={blocker.end_date ? `Due ${shortDate(blocker.end_date)}` : undefined}
    >
      {done && <CheckCircle2 className="h-3 w-3" />}
      <span className="truncate max-w-[220px]">{blocker.title}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove blocker"
        className="opacity-50 transition-opacity hover:opacity-100"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// The "Blocked by" editor — chip list + a searchable picker of sibling
// tasks. Add/remove fire immediately (no Save needed), mirroring the
// milestone-blocker behaviour.
function TaskBlockerEditor({ task, allTasks, onAdd, onRemove }: {
  task: Task;
  allTasks: Task[];
  onAdd: (blockerTaskId: number) => void;
  onRemove: (blockerTaskId: number) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const blockers = task.blockers ?? [];
  const existing = new Set(blockers.map((b) => b.id));

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allTasks
      .filter((t) => t.id !== task.id && !existing.has(t.id))
      .filter((t) => !q || t.title.toLowerCase().includes(q))
      .slice(0, 50);
    // `blockers` drives `existing`; depend on it so the list refreshes.
  }, [allTasks, task.id, query, blockers]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        {blockers.length === 0 && (
          <span className="text-xs italic text-ink-subtle">Nothing blocking this task.</span>
        )}
        {blockers.map((b) => (
          <TaskBlockerChip key={b.dep_id} blocker={b} onRemove={() => onRemove(b.id)} />
        ))}
      </div>
      {picking ? (
        <div className="mt-2 overflow-hidden rounded-md border border-line">
          <div className="relative border-b border-line-soft">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-subtle" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape") { setPicking(false); setQuery(""); } }}
              placeholder="Search tasks in this plan…"
              className="w-full py-1.5 pl-8 pr-3 text-xs text-ink-2 placeholder:text-ink-subtle focus:outline-none"
            />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {candidates.length === 0 && (
              <div className="px-3 py-3 text-center text-[11px] italic text-ink-subtle">
                {query ? "No matching tasks" : "No other tasks to depend on"}
              </div>
            )}
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onAdd(c.id); setPicking(false); setQuery(""); }}
                className="flex w-full items-center justify-between gap-2 border-t border-line-soft px-3 py-1.5 text-left text-xs first:border-t-0 hover:bg-raised"
              >
                <span className="truncate text-ink">{c.title}</span>
                <span className={cn(
                  "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                  STATUS_BG[c.status ?? "not_started"],
                )}>
                  {STATUS_LABEL[c.status ?? "not_started"] ?? c.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="mt-2 flex items-center gap-1 rounded-md border border-dashed border-line-strong px-2 py-1 text-[11px] font-medium text-ink-muted transition-colors hover:border-brand-navy-400 hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
        >
          <Plus className="h-3 w-3" /> Add blocker
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Task Modal
// ---------------------------------------------------------------------------
function TaskModal({ task, milestones, defaultMilestoneId, allTasks, onAddBlocker, onRemoveBlocker, onClose, onSave, onDelete }: {
  task: Task | null; milestones: Milestone[]; defaultMilestoneId: number | null;
  allTasks: Task[];
  onAddBlocker: (taskId: number, blockerTaskId: number) => void;
  onRemoveBlocker: (taskId: number, blockerTaskId: number) => void;
  onClose: () => void; onSave: (fields: Partial<Task>) => void; onDelete?: () => void;
}) {
  const [form, setForm] = useState({
    title: task?.title ?? "",
    milestone_id: task?.milestone_id ?? defaultMilestoneId ?? "",
    assignee: task?.assignee ?? "",
    status: task?.status ?? "not_started",
    priority: task?.priority ?? "Medium",
    start_date: task?.start_date ?? "",
    end_date: task?.end_date ?? "",
    est_hours: String(task?.est_hours ?? 0),
    notes: task?.notes ?? "",
  });

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const cls = "w-full rounded-md border border-line bg-surface px-3 py-2 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-300";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl bg-surface p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-ink">{task ? "Edit Task" : "New Task"}</div>
          <button onClick={onClose} className="text-ink-subtle hover:text-ink-3"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Title</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} className={cls} autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Phase</label>
              <select value={form.milestone_id} onChange={(e) => set("milestone_id", e.target.value)} className={cls}>
                <option value="">— none —</option>
                {milestones.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Assignee</label>
              <PersonPicker
                value={form.assignee}
                onChange={(v) => set("assignee", v)}
                placeholder="Name"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={cls}>
                <option value="not_started">Not started</option>
                <option value="in_progress">Working</option>
                <option value="blocked">Blocked</option>
                <option value="complete">Complete</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Priority</label>
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={cls}>
                <option value="Highest">Highest</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Est. Hours</label>
              <input type="number" min={0} step={1} value={form.est_hours} onChange={(e) => set("est_hours", e.target.value)} className={cls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Start Date</label>
              <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={cls} />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">End Date</label>
              <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={cls} />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={cn(cls, "resize-none")} />
          </div>
          {/* Blocked by — task dependencies. Only on an existing task: a
              new task has no id yet to hang edges off. Add/remove fire
              immediately, independent of the Save button. */}
          {task && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Blocked by</label>
              <TaskBlockerEditor
                task={task}
                allTasks={allTasks}
                onAdd={(blockerTaskId) => onAddBlocker(task.id, blockerTaskId)}
                onRemove={(blockerTaskId) => onRemoveBlocker(task.id, blockerTaskId)}
              />
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center justify-between">
          {onDelete ? (
            <button onClick={onDelete} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-300 transition-colors">
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised transition-colors">Cancel</button>
            <button
              onClick={() => {
                if (!form.title.trim()) { toast.error("Title is required"); return; }
                onSave({
                  title: form.title.trim(),
                  milestone_id: form.milestone_id ? Number(form.milestone_id) : null,
                  assignee: form.assignee || null,
                  status: form.status,
                  priority: form.priority,
                  start_date: form.start_date || null,
                  end_date: form.end_date || null,
                  est_hours: parseFloat(form.est_hours) || 0,
                  notes: form.notes || null,
                } as any);
              }}
              className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 transition-colors"
            >
              <Save className="h-3 w-3" /> {task ? "Save" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
